<template>
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
    />
  </div>
  <page-viewer
    v-for="(page, index) in storyPages"
    v-else
    v-show="index === currentPageIndex"
    :key="index"
    :page-content="page.content"
    :page-id="index"
    :ratio="page.ratio"
    :class="index === currentPageIndex ? 'active' : ''"
    @user-interaction="onUserInteraction"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import _ from 'lodash';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import anonymousSession from '../../anonymousSession';
import { completeDescopeOtsSignup } from '../../api/loginApi';
import { getOtsSignupStoryByName } from '../../api/sumsumApi';
import useContentLoader from '../../composables/useContentLoader';
import Segment from '../../Segment';
import { useOtsJob } from '../../services/otsConfigService';
import { useSessionStore } from '../../stores/session';
import storyLib from '../StoryLib';

const { PageViewer } = storyLib;

const route = useRoute();
const router = useRouter();
const sessionStore = useSessionStore();
const storyPages = ref([]);
const currentPageIndex = ref(0);
const story = ref(null);
const utmParams = _.pickBy(route.query, (value, key) => (key.startsWith('utm_') && value));

const { setOtsFlowConfig, setOtsJobConfig } = useOtsJob();
const {
  showContentLoader, toggleLoader,
} = useContentLoader();

const onUserInteraction = async data => {
  if (data && data.eventName === 'linkClicked' && data.link) {
    Segment.trackUserGot('OtsSignup_LinkClicked', { otsFlowConfig: sessionStore.otsFlowConfig, link: data.link });
    window.open(data.link, '_blank');
  }

  if (data && data.eventName === 'nextPage') {
    nextPage();
  }

  if (data && data.eventName === 'previousPage') {
    previousPage();
  }

  if (data && data.eventName === 'signupError') {
    // console.log('signupError', data);
    Segment.trackUserGot('OtsSignup_SignupError', { otsFlowConfig: sessionStore.otsFlowConfig, error: data.error });
  }

  if (data && data.eventName === 'signupSuccess') {
    Segment.trackUserGot('OtsSignup_SignupSuccess', { otsFlowConfig: sessionStore.otsFlowConfig, user: data.user });
    await handleDescopeSignupSuccess(data);
  }
};

const handleDescopeSignupSuccess = async event => {
  toggleLoader(true);

  const token = event.data.sessionJwt;
  const { market } = event.data.user.customAttributes;
  const { anonymousUserId } = event.data.user.customAttributes;
  anonymousSession.setAnonymousUserId(anonymousUserId);

  if (!token) {
    throw new Error('Descope Signup flow failed to signup, user sessionJwt is empty');
  }

  const { jobId, customerId, memberId } = await completeDescopeOtsSignup(
    token,
    utmParams,
    {
      flowConfigId: sessionStore.otsFlowConfig?.flowId,
    },
    Cookies.get('gclid'),
  );

  Segment.identifyCustomer(customerId, memberId);
  Segment.trackUserGot('CustomerCreated_Marketing', {
    market,
    product: 'ots',
    gclid: Cookies.get('gclid'),
    customerId,
    utmParams,
    otsParams: {
      flowConfigId: sessionStore.otsFlowConfig?.flowId,
    },
  });

  setOtsJobConfig(sessionStore.otsFlowConfig?.flowId, jobId);
  setOtsFlowConfig(sessionStore.otsFlowConfig);
  router.push('/ots/connect');
};

const previousPage = async () => {
  if (currentPageIndex.value > 0) {
    currentPageIndex.value -= 1;
  }
};

const nextPage = async () => {
  if (currentPageIndex.value < storyPages.value.length - 1) {
    Segment.trackUserInteraction('OtsSignup_NextPageClicked', {
      otsFlowConfig: sessionStore.otsFlowConfig,
      nextPage: storyPages.value[currentPageIndex.value + 1]?.name,
    });

    // Move to next page
    currentPageIndex.value += 1;
  } else {
    // This is the last page
    Segment.trackUserInteraction('OtsSignup_Done', {
      otsFlowConfig: sessionStore.otsFlowConfig,
    });
  }
};
onMounted(async () => {
  toggleLoader(true);

  story.value = await getOtsSignupStoryByName(sessionStore.otsFlowConfig?.signupPageFlow.flowId);

  if (story.value) {
    const storyData = typeof story.value === 'string'
      ? JSON.parse(story.value)
      : story.value;

    storyPages.value = storyData.pages || [];
  }
  toggleLoader(false);
  Segment.trackUserGot('OtsSignup_PageEntered', {
    otsFlowConfig: sessionStore.otsFlowConfig,
  });
});

</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';
.page {
  background-color: #E1F1F2;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
